import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import DeleteModal from "../../components/Common/DeleteModal";

import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  FormGroup,
  Label,
  Button,
  Offcanvas,
  OffcanvasHeader,
  OffcanvasBody,
} from "reactstrap";

import { ToastContainer } from "react-toastify";
import { Formik, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import { map } from "lodash";

import ActionEdit from "components/buttons/actionEdit";
import ActionDelete from "components/buttons/actionDelete";
import SafeField from "helpers/SafeField";

import {
  addListingType,
  getListingType,
  updateListingType,
  deleteListingType,
} from "../../store/actions";

const ListingType = () => {
  const dispatch = useDispatch();

  const { t } = useTranslation();

  const { listingtypes, loading } = useSelector(
    (state) => state.ObjectReducer
  );

  const [listingType, setListingType] = useState({});
  const [isEdit, setIsEdit] = useState(false);
  const [isRight, setIsRight] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);

  // =============================
  // Load Listing Types
  // =============================
  useEffect(() => {
    const userId = localStorage.getItem("userId");
    dispatch(getListingType(userId));
  }, [dispatch]);

  // =============================
  // Canvas Controls
  // =============================
  const openEditCanvas = (data) => {
    setListingType({
      id: data.id,
      listingTypeId: data.listingTypeId,
      listingTypeTitle: data.listingTypeTitle,
      agentId: data.agentId,
      isActive: data.isActive,
    });
    setIsEdit(true);
    setIsRight(true);
  };

  const closeCanvas = () => {
    setListingType({});
    setIsEdit(false);
    setIsRight(false);
  };

  // =============================
  // Add Submit
  // =============================
  const handleListingTypeSubmit = (values, { resetForm }) => {
    dispatch(addListingType(values, history));
    resetForm();
  };

  // =============================
  // Delete
  // =============================
  const onClickDelete = (item) => {
    setListingType(item);
    setDeleteModal(true);
  };

  const handleDeleteListingType = () => {
    if (listingType?.id) {
      dispatch(deleteListingType(listingType));
      setDeleteModal(false);
    }
  };

  // =============================
  // Update Submit
  // =============================
  const handleUpdate = (values) => {
    const updateData = {
      id: values.id,
      listingTypeId: values.listingTypeId,
      listingTypeTitle: values.listingTypeTitle,
    };

    dispatch(updateListingType(updateData));
    closeCanvas();
  };

  return (
    <React.Fragment>
      {loading && <div className="loading">Loading…</div>}
      <ToastContainer autoClose={2000} />

      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeleteListingType}
        onCloseClick={() => setDeleteModal(false)}
      />

      <div className="page-content">
       
        <Container fluid>
          <Row className="mb20">
            <Col xl="12">
              <Breadcrumbs
                title={t("General Settings")}
                breadcrumbItem={t("Listing Type")}
              />
            </Col>
          </Row>

          <Row>
           
            <Col xl="12">
              {/* ================= Add Form ================= */}
              <Formik
                initialValues={{
                  listingTypeTitle: "",
                  agentId: Number(localStorage.getItem("userId")),
                }}
                validationSchema={Yup.object({
                  listingTypeTitle: Yup.string().required("This is required"),
                })}
                onSubmit={handleListingTypeSubmit}
              >
                {({ errors, touched, handleChange }) => (
                  <Form className="needs-validation">
                    <Card>
                      <CardBody>
                        
                        <Row>
                          <Col sm="3">
                            <FormGroup className="mb-3">
                              <Label>{t("Title *")}</Label>

                              <SafeField
                                name="listingTypeTitle"
                                type="text"
                                onChange={handleChange}
                                className={
                                  "form-control" +
                                  (errors.listingTypeTitle &&
                                  touched.listingTypeTitle
                                    ? " is-invalid"
                                    : "")
                                }
                              />

                              <ErrorMessage
                                name="listingTypeTitle"
                                component="div"
                                className="invalid-feedback"
                              />
                            </FormGroup>
                          </Col>

                          <Col sm="2">
                            <Button
                              type="submit"
                              color="primary"
                              style={{ marginTop: 31 }}
                            >
                              Add
                            </Button>
                          </Col>
                        </Row>
                      </CardBody>
                    </Card>

                    {/* ================= History ================= */}
                    <Card>
                      <CardBody>
                        <h5 className="card-title">History</h5>

                        <div className="table-responsive">
                          <table className="table table-bordered">
                            <thead>
                              <tr>
                                <th>#</th>
                                <th>Title</th>
                                <th>Action</th>
                              </tr>
                            </thead>

                            <tbody>
                              {map(listingtypes, (item, index) => (
                                <tr key={index}>
                                  <td>{index + 1}</td>
                                  <td>{item.listingTypeTitle}</td>
                                  <td>
                                    <ActionEdit
                                      onClick={() => openEditCanvas(item)}
                                    />
                                    <ActionDelete
                                      onClick={() => onClickDelete(item)}
                                    />
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </CardBody>
                    </Card>
                  </Form>
                )}
              </Formik>
            </Col>
          </Row>

          {/* ================= Edit Offcanvas ================= */}
          <Offcanvas isOpen={isRight} direction="end" toggle={closeCanvas}>
            <OffcanvasHeader toggle={closeCanvas}>
              Update
            </OffcanvasHeader>

            <OffcanvasBody>
              <Formik
                enableReinitialize
                initialValues={{
                  id: listingType?.id,
                  listingTypeId: listingType?.listingTypeId,
                  listingTypeTitle: listingType?.listingTypeTitle || "",
                  agentId: listingType?.agentId,
                  isActive: listingType?.isActive,
                }}
                validationSchema={Yup.object({
                  listingTypeTitle: Yup.string().required("This is required"),
                })}
                onSubmit={handleUpdate}
              >
                {({ errors, touched, handleChange }) => (
                  <Form>
                    <FormGroup className="mb-3">
                      <Label>{t("Title *")}</Label>

                      <SafeField
                        name="listingTypeTitle"
                        type="text"
                        onChange={handleChange}
                        className={
                          "form-control" +
                          (errors.listingTypeTitle &&
                          touched.listingTypeTitle
                            ? " is-invalid"
                            : "")
                        }
                      />

                      <ErrorMessage
                        name="listingTypeTitle"
                        component="div"
                        className="invalid-feedback"
                      />
                    </FormGroup>

                    <Button type="submit" color="primary">
                      Update
                    </Button>
                  </Form>
                )}
              </Formik>
            </OffcanvasBody>
          </Offcanvas>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default ListingType;
